import { db, type CatalogCache } from "./offline-db";
import { apiGet } from "./api";

/**
 * Default max age for cached data: 24 hours.
 * Individual callers can override with maxAgeMs parameter.
 */
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Endpoints to pre-cache on each successful online session.
 * Keys are used as IndexedDB cache keys (prefixed with "offline:" to avoid
 * collision with the catalog-cache.ts keys that use bare names).
 */
const PRE_CACHE_ENDPOINTS: Record<string, string> = {
  "offline:projects": "/api/projects?status=active",
  "offline:cert-types": "/api/certifications/types",
  "offline:my-certs": "/api/certifications/mine",
  "offline:divisions": "/api/divisions",
  "offline:job-titles": "/api/job-titles",
};

/**
 * Store a value in the offline cache.
 */
export async function cacheData(key: string, data: unknown): Promise<void> {
  const now = new Date().toISOString();
  const expiresAt = new Date(Date.now() + DEFAULT_MAX_AGE_MS).toISOString();
  await db.catalogs.put({ key, data, cachedAt: now, expiresAt });
}

/**
 * Retrieve a cached value from IndexedDB.
 * Returns null if missing or older than maxAgeMs (defaults to 24h).
 */
export async function getCachedData<T>(
  key: string,
  maxAgeMs: number = DEFAULT_MAX_AGE_MS
): Promise<T | null> {
  try {
    const entry = await db.catalogs.get(key);
    if (!entry || !entry.data) return null;

    const age = Date.now() - new Date(entry.cachedAt).getTime();
    if (age > maxAgeMs) return null;

    return entry.data as T;
  } catch {
    return null;
  }
}

/**
 * Retrieve cached data with no staleness check — returns whatever is stored,
 * even if expired. Useful as an absolute fallback when offline.
 */
export async function getCachedDataStale<T>(key: string): Promise<T | null> {
  try {
    const entry = await db.catalogs.get(key);
    if (!entry || !entry.data) return null;
    return entry.data as T;
  } catch {
    return null;
  }
}

/**
 * Fetch a single endpoint and cache the result.
 * Returns the data on success, null on failure.
 */
async function fetchAndCacheEndpoint(
  key: string,
  endpoint: string
): Promise<unknown | null> {
  try {
    const data = await apiGet<unknown>(endpoint);
    await cacheData(key, data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Pre-fetch and cache ALL offline data endpoints.
 * Call on app startup / successful session validation when online.
 * Uses Promise.allSettled so one failure doesn't block others.
 */
export async function preCacheAll(): Promise<void> {
  const entries = Object.entries(PRE_CACHE_ENDPOINTS);
  await Promise.allSettled(
    entries.map(([key, endpoint]) => fetchAndCacheEndpoint(key, endpoint))
  );
}

/**
 * Clear all offline-prefixed cache entries.
 */
export async function clearAllCached(): Promise<void> {
  try {
    const allEntries = await db.catalogs.toArray();
    const offlineKeys = allEntries
      .filter((e) => e.key.startsWith("offline:"))
      .map((e) => e.key);
    await db.catalogs.bulkDelete(offlineKeys);
  } catch {
    // Silently ignore
  }
}
